import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/logo.png";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { supabase } from "../lib/supabase";

const emptyForm = {
  fullName: "",
  email: "",
  propertyAddress: "",
  unit: "",
  residencyType: "owner",
  note: "",
  website: "",
};

export function ResidentRequestAccessPage() {
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const handleTurnstileToken = useCallback((token) => setTurnstileToken(token), []);
  const handleTurnstileError = useCallback((message) => setError(message), []);
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    if (!turnstileToken) return setError("Complete the security verification before submitting your request.");
    setSubmitting(true);
    const { data, error: requestError } = await supabase.functions.invoke("request-resident-access", {
      body: {
        fullName: form.fullName,
        email: form.email,
        propertyAddress: form.propertyAddress,
        unit: form.unit || null,
        residencyType: form.residencyType,
        note: form.note || null,
        website: form.website || null,
        turnstileToken,
      },
    });
    setSubmitting(false);
    if (requestError || data?.error) {
      setError(data?.error || requestError?.message || "We could not submit the request. Please try again.");
      setTurnstileToken("");
      setTurnstileResetKey((current) => current + 1);
      return;
    }
    setForm(emptyForm);
    setSubmitted(true);
  }

  return (
    <div className="screen centered access-request-screen">
      <section className="card access-request-card" aria-labelledby="resident-request-title">
        <div className="access-request-heading">
          <img src={logo} width="150" height="100" alt="Coach Homes III COA" />
          <div>
            <span className="eyebrow">Private resident documents</span>
            <h1 id="resident-request-title">Request Resident Center Access</h1>
            <p>Access is limited to verified Coach Homes III residents and requires administrator approval.</p>
          </div>
        </div>

        {submitted ? (
          <section className="access-request-success" aria-live="polite">
            <h2>Request received</h2>
            <p>An administrator will verify the information. If approved, you will receive a secure account invitation by email.</p>
            <p>For privacy, this page does not confirm whether an email address already has an account or pending request.</p>
            <Link className="button" to="/login">Return to Sign In</Link>
          </section>
        ) : (
          <form className="access-request-form" onSubmit={handleSubmit}>
            {error ? <div className="error-block" role="alert">{error}</div> : null}
            <label>Full name<input required minLength="2" maxLength="100" autoComplete="name" value={form.fullName} onChange={(event) => update("fullName", event.target.value)} /></label>
            <label>Email address<input required maxLength="254" type="email" autoComplete="email" value={form.email} onChange={(event) => update("email", event.target.value)} /></label>
            <div className="resident-address-grid">
              <label>Coach Homes III property address<input required minLength="3" maxLength="200" autoComplete="street-address" value={form.propertyAddress} onChange={(event) => update("propertyAddress", event.target.value)} placeholder="Street address" /></label>
              <label>Unit number<input required minLength="1" maxLength="40" autoComplete="address-line2" value={form.unit} onChange={(event) => update("unit", event.target.value)} /></label>
            </div>
            <label>Residency type<select required value={form.residencyType} onChange={(event) => update("residencyType", event.target.value)}><option value="owner">Owner</option><option value="tenant">Tenant</option><option value="authorized_resident">Other authorized resident</option></select></label>
            <label>Verification note <span className="optional-label">Optional</span><textarea maxLength="1000" rows="4" value={form.note} onChange={(event) => update("note", event.target.value)} placeholder="Add information that may help the administrator verify your residency." /></label>
            <label className="request-honeypot" aria-hidden="true">Website<input tabIndex="-1" autoComplete="off" value={form.website} onChange={(event) => update("website", event.target.value)} /></label>
            <div className="access-request-notice">Submitting this form does not create an account. Every request is verified before an invitation is sent.</div>
            <TurnstileWidget action="request_resident_access" siteKey={turnstileSiteKey} onToken={handleTurnstileToken} onError={handleTurnstileError} resetKey={turnstileResetKey} />
            <button type="submit" disabled={submitting || !turnstileToken}>{submitting ? "Submitting request…" : "Submit Resident Access Request"}</button>
            <Link className="access-request-back" to="/login">Already have access? Return to sign in</Link>
          </form>
        )}
      </section>
    </div>
  );
}
